import { NgControl, FormControlName, FormGroupDirective } from '@angular/forms';
import { isValidCPF, isValidCNPJ } from '@brazilian-utils/brazilian-utils';
import { FieldTypeMap, FieldType, labelToControlName, FieldDataType, FieldDataTypeMap } from '@elotech/angular-components/types';
import { v4 } from 'uuid';

function isRequired(control) {
    if (!control || !control.validator) {
        return false;
    }
    const validator = control.validator({});
    return validator && validator['required'];
}
function getFormControlFrom(injector) {
    const ngControl = injector.get(NgControl, null);
    if (!ngControl) {
        return null;
    }
    if (ngControl instanceof FormControlName) {
        return injector.get(FormGroupDirective).getControl(ngControl);
    }
    else {
        return ngControl.form;
    }
}
function getNameInitials(name) {
    const fullname = name.split(" ");
    return fullname.map((n, i) => (i == 0 || i == fullname.length - 1) && n[0]).filter(n => n).join("");
}
function sortNumberNull(arr, prop, ascending = true) {
    if (!prop)
        return arr;
    const nullValueReplacer = ascending ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
    const sorter = function (a, b) {
        const aProp = a[prop] === null || a[prop] === undefined ? nullValueReplacer : a[prop];
        const bProp = b[prop] === null || b[prop] === undefined ? nullValueReplacer : b[prop];
        return ascending ? aProp - bProp : bProp - aProp;
    };
    return arr.sort(sorter);
}
function niceBytes(x) {
    const units = ['bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    let l = 0, n = parseInt(x + '', 10) || 0;
    while (n >= 1024 && ++l) {
        n = n / 1024;
    }
    return (n.toFixed(n < 10 && l > 0 ? 1 : 0) + ' ' + units[l]);
}
function highlight(elem, keywords, caseSensitive = false, firstCall = true, cls = 'text-highlight') {
    const keywordsFiltered = keywords.filter(keyword => keyword && keyword.length > 0);
    if (firstCall) {
        document.querySelectorAll(`.${cls}`).forEach((el) => {
            const parent = el.parentNode;
            if (!parent) {
                return;
            }
            parent.replaceChild(document.createTextNode(el.textContent || ''), el);
            parent.normalize();
        });
    }
    if (keywordsFiltered.length === 0) {
        return;
    }
    const flags = caseSensitive ? 'g' : 'gi';
    keywordsFiltered.sort((a, b) => b.length - a.length);
    Array.from(elem.childNodes).forEach((child) => {
        const keywordRegex = RegExp(keywordsFiltered.join('|'), flags);
        const notTextNode = child.nodeType !== 3;
        if (notTextNode) {
            highlight(child, keywordsFiltered, caseSensitive, false, cls);
        }
        else if (keywordRegex.test(child.textContent)) {
            const frag = document.createDocumentFragment();
            let lastIdx = 0;
            child.textContent.replace(keywordRegex, (match, idx) => {
                const part = document.createTextNode(child.textContent.slice(lastIdx, idx));
                const highlighted = document.createElement('mark');
                highlighted.textContent = match;
                highlighted.classList.add(cls);
                frag.appendChild(part);
                frag.appendChild(highlighted);
                lastIdx = idx + match.length;
            });
            const end = document.createTextNode(child.textContent.slice(lastIdx));
            frag.appendChild(end);
            child.parentNode.replaceChild(frag, child);
        }
    });
}

class DocumentsValidators {
    static { this.validateMethods = {
        cpf: isValidCPF,
        cnpj: isValidCNPJ
    }; }
    static isValidCpfCnpj(control) {
        const value = control.value;
        if (value && value.length === 11) {
            return isValidCPF(value) ? null : { invalidCpf: 'CPF inválido' };
        }
        if (value && value.length === 14) {
            return isValidCNPJ(value) ? null : { invalidCnpj: 'CNPJ inválido' };
        }
        if (value && value.length !== 11 && value.length !== 14) {
            return { invalidSize: 'Deve ter 11 ou 14 dígitos' };
        }
        return null;
    }
    static isValidDocument(document) {
        return (control) => {
            const value = control.value;
            if (value) {
                const invalidKey = 'invalid' + document.charAt(0).toUpperCase() + document.slice(1);
                return this.validateMethods[document](value) ? null : JSON.parse(`{"${invalidKey}": "Valor inválido para ${document.toUpperCase()}"}`);
            }
            return null;
        };
    }
}

const currentUser = {
    name: 'Fulano de Tal',
    email: 'fulano.de.tal@teste.com'
};
const dynamicFormWithAllDefaultFields = Array.from(FieldTypeMap.values()).map((fieldType) => {
    const fieldTypeEnum = FieldType[fieldType.enumName];
    return {
        id: v4(),
        label: fieldType.name,
        controlName: labelToControlName(fieldType.name, FieldDataType[fieldType.dataType], null),
        type: fieldTypeEnum,
        required: true,
        placeholder: '',
        description: fieldTypeEnum === FieldType.INFO ? 'Descrição' : '',
        mask: '',
        config: {},
        options: [FieldType.OPTIONS, FieldType.SELECT].includes(fieldTypeEnum) ? ['A', 'B', 'C'] : [],
        fields: fieldTypeEnum === FieldType.FIELD_SET ? [{
                id: v4(),
                label: "Texto",
                type: FieldType.TEXT,
                controlName: labelToControlName("Texto", FieldDataType.TEXT, null),
                required: false,
                placeholder: '',
                description: '',
                mask: '',
                config: {},
                options: [],
                fields: [],
                conditions: [],
            }] : [],
        conditions: [],
    };
});
const dynamicFormWithAllFieldsNotRequired = dynamicFormWithAllDefaultFields.map(field => {
    return {
        ...field,
        required: false
    };
});
function getFillFieldsValues(fields) {
    return fields.filter(f => FieldTypeMap.get(f.type)?.field).reduce((acc, field) => {
        acc[field.controlName || field.label] = getSampleValueFromField(field);
        return acc;
    }, {});
}
const unidades = [{ "id": 28, "codigo": "001", "descricao": "GABINETE DO SECRETARIO DE SERVIÇO SOCIAL", "unidadeComposta": "08001", "unidadeCompostaFormatada": "08001", "unidadeSuperior": { "id": 8 } }, { "id": 18, "codigo": "001", "descricao": "GABINETE DA SECRETARIA DE FINANÇAS", "unidadeComposta": "06001", "unidadeCompostaFormatada": "06001", "unidadeSuperior": { "id": 6 } }, { "id": 19, "codigo": "001", "descricao": "GABINETE DO ASSESSOR DE PLANEJAMENTO", "unidadeComposta": "04001", "unidadeCompostaFormatada": "04001", "unidadeSuperior": { "id": 4 } }, { "id": 20, "codigo": "001", "descricao": "GABINETE DO ASSESSOR JURÍDICO", "unidadeComposta": "03001", "unidadeCompostaFormatada": "03001", "unidadeSuperior": { "id": 3 } }, { "id": 21, "codigo": "001", "descricao": "GABINETE DO PREFEITO", "unidadeComposta": "02001", "unidadeCompostaFormatada": "02001", "unidadeSuperior": { "id": 2 } }, { "id": 22, "codigo": "001", "descricao": "GABINETE DO SECRETARIO DE AGRICULTURA E PECUÁRIA", "unidadeComposta": "12001", "unidadeCompostaFormatada": "12001", "unidadeSuperior": { "id": 12 } }, { "id": 23, "codigo": "001", "descricao": "GABINETE DO SECRETARIO DE CULTURA", "unidadeComposta": "10001", "unidadeCompostaFormatada": "10001", "unidadeSuperior": { "id": 10 } }, { "id": 24, "codigo": "001", "descricao": "GABINETE DO SECRETARIO DE EDUCAÇÃO", "unidadeComposta": "09001", "unidadeCompostaFormatada": "09001", "unidadeSuperior": { "id": 9 } }, { "id": 25, "codigo": "001", "descricao": "GABINETE DO SECRETARIO DE ESPORTES", "unidadeComposta": "16001", "unidadeCompostaFormatada": "16001", "unidadeSuperior": { "id": 16 } }, { "id": 26, "codigo": "001", "descricao": "GABINETE DO SECRETARIO DE MEIO AMBIENTE", "unidadeComposta": "15001", "unidadeCompostaFormatada": "15001", "unidadeSuperior": { "id": 15 } }, { "id": 27, "codigo": "001", "descricao": "GABINETE DO SECRETARIO DE SAÚDE", "unidadeComposta": "07001", "unidadeCompostaFormatada": "07001", "unidadeSuperior": { "id": 7 } }, { "id": 29, "codigo": "001", "descricao": "GABINETE DO SECRETARIO DE VIAÇÃO, OBRAS E SERVIÇOS URBANOS", "unidadeComposta": "11001", "unidadeCompostaFormatada": "11001", "unidadeSuperior": { "id": 11 } }, { "id": 32, "codigo": "001", "descricao": "LEGISLATIVO MUNICIPAL", "unidadeComposta": "01001", "unidadeCompostaFormatada": "01001", "unidadeSuperior": { "id": 1 } }, { "id": 31, "codigo": "001", "descricao": "INST. PREV. ASSISTÊNCIA DO MUNICÍPIO DE ANGULO", "unidadeComposta": "13001", "unidadeCompostaFormatada": "13001", "unidadeSuperior": { "id": 13 } }, { "id": 30, "codigo": "001", "descricao": "SERVIÇO AUTONOMO MUNICIPAL DE AGUA E ESGOTO", "unidadeComposta": "14001", "unidadeCompostaFormatada": "14001", "unidadeSuperior": { "id": 14 } }, { "id": 17, "codigo": "001", "descricao": "GABINETE DA SECRETARIA DE ADMINISTRAÇÃO", "unidadeComposta": "05001", "unidadeCompostaFormatada": "05001", "unidadeSuperior": { "id": 5 } }, { "id": 43, "codigo": "002", "descricao": "DIVISÃO DE TESOURARIA", "unidadeComposta": "06002", "unidadeCompostaFormatada": "06002", "unidadeSuperior": { "id": 6 } }, { "id": 42, "codigo": "002", "descricao": "DIVISÃO DE SERVIÇO SOCIAL", "unidadeComposta": "08002", "unidadeCompostaFormatada": "08002", "unidadeSuperior": { "id": 8 } }, { "id": 41, "codigo": "002", "descricao": "DIVISÃO DE SAÚDE", "unidadeComposta": "07002", "unidadeCompostaFormatada": "07002", "unidadeSuperior": { "id": 7 } }, { "id": 40, "codigo": "002", "descricao": "DIVISÃO DE OBRAS", "unidadeComposta": "11002", "unidadeCompostaFormatada": "11002", "unidadeSuperior": { "id": 11 } }, { "id": 39, "codigo": "002", "descricao": "DIVISÃO DE MEIO AMBIENTE", "unidadeComposta": "15002", "unidadeCompostaFormatada": "15002", "unidadeSuperior": { "id": 15 } }, { "id": 38, "codigo": "002", "descricao": "DIVISÃO DE LICITAÇÕES, COMPRAS E PATRIMONIO", "unidadeComposta": "05002", "unidadeCompostaFormatada": "05002", "unidadeSuperior": { "id": 5 } }, { "id": 37, "codigo": "002", "descricao": "DIVISÃO DE ESPORTES", "unidadeComposta": "16002", "unidadeCompostaFormatada": "16002", "unidadeSuperior": { "id": 16 } }, { "id": 36, "codigo": "002", "descricao": "DIVISÃO DE CULTURA", "unidadeComposta": "10002", "unidadeCompostaFormatada": "10002", "unidadeSuperior": { "id": 10 } }, { "id": 35, "codigo": "002", "descricao": "DIVISÃO DE AGRICULTURA E PECUÁRIA", "unidadeComposta": "12002", "unidadeCompostaFormatada": "12002", "unidadeSuperior": { "id": 12 } }, { "id": 34, "codigo": "002", "descricao": "ASSISTÊNCIA A CRIANÇA E AO ADOLESCENTE", "unidadeComposta": "09002", "unidadeCompostaFormatada": "09002", "unidadeSuperior": { "id": 9 } }, { "id": 33, "codigo": "002", "descricao": "ASSESSORIA JURÍDICA", "unidadeComposta": "02002", "unidadeCompostaFormatada": "02002", "unidadeSuperior": { "id": 2 } }, { "id": 45, "codigo": "003", "descricao": "DIVISÃO DE CONTABILIDADE", "unidadeComposta": "06003", "unidadeCompostaFormatada": "06003", "unidadeSuperior": { "id": 6 } }, { "id": 44, "codigo": "003", "descricao": "ASSESSORIA DE PLANEJAMENTO", "unidadeComposta": "02003", "unidadeCompostaFormatada": "02003", "unidadeSuperior": { "id": 2 } }, { "id": 46, "codigo": "003", "descricao": "DIVISÃO DE ESPORTES", "unidadeComposta": "10003", "unidadeCompostaFormatada": "10003", "unidadeSuperior": { "id": 10 } }, { "id": 47, "codigo": "003", "descricao": "DIVISÃO DE MEIO AMBIENTE", "unidadeComposta": "12003", "unidadeCompostaFormatada": "12003", "unidadeSuperior": { "id": 12 } }, { "id": 48, "codigo": "003", "descricao": "DIVISÃO DE RECURSOS HUMANOS", "unidadeComposta": "05003", "unidadeCompostaFormatada": "05003", "unidadeSuperior": { "id": 5 } }, { "id": 49, "codigo": "003", "descricao": "DIVISÃO DE SERVIÇOS GERAIS", "unidadeComposta": "11003", "unidadeCompostaFormatada": "11003", "unidadeSuperior": { "id": 11 } }, { "id": 50, "codigo": "003", "descricao": " FUNDO MUNICIPAL DE ASSISTÊNCIA SOCIAL DE ÂNGULO", "unidadeComposta": "08003", "unidadeCompostaFormatada": "08003", "unidadeSuperior": { "id": 8 } }, { "id": 51, "codigo": "003", "descricao": "FUNDO MUNICIPAL DE EDUCAÇÃO DE ÂNGULO", "unidadeComposta": "09003", "unidadeCompostaFormatada": "09003", "unidadeSuperior": { "id": 9 } }, { "id": 52, "codigo": "003", "descricao": "FUNDO MUNICIPAL DE SAÚDE DE ÂNGULO", "unidadeComposta": "07003", "unidadeCompostaFormatada": "07003", "unidadeSuperior": { "id": 7 } }, { "id": 57, "codigo": "004", "descricao": "DIVISÃO DE SERVIÇOS RODOVIÁRIOS MUNICIPAL", "unidadeComposta": "11004", "unidadeCompostaFormatada": "11004", "unidadeSuperior": { "id": 11 } }, { "id": 53, "codigo": "004", "descricao": "CONTROLADORIA INTERNA", "unidadeComposta": "02004", "unidadeCompostaFormatada": "02004", "unidadeSuperior": { "id": 2 } }, { "id": 54, "codigo": "004", "descricao": "DIVISÃO DE EDUCACAO INFANTIL", "unidadeComposta": "09004", "unidadeCompostaFormatada": "09004", "unidadeSuperior": { "id": 9 } }, { "id": 55, "codigo": "004", "descricao": "DIVISÃO DE FISCALIZAÇÃO, CADASTRO E TRIBUTAÇÃO", "unidadeComposta": "06004", "unidadeCompostaFormatada": "06004", "unidadeSuperior": { "id": 6 } }, { "id": 56, "codigo": "004", "descricao": "DIVISÃO DE SERVIÇOS GERAIS", "unidadeComposta": "05004", "unidadeCompostaFormatada": "05004", "unidadeSuperior": { "id": 5 } }, { "id": 58, "codigo": "005", "descricao": "SERVIÇO MILITAR", "unidadeComposta": "02005", "unidadeCompostaFormatada": "02005", "unidadeSuperior": { "id": 2 } }, { "id": 1, "codigo": "01", "descricao": "LEGISLATIVO MUNICIPAL", "unidadeComposta": "01", "unidadeCompostaFormatada": "01", "unidadeSuperior": null }, { "id": 2, "codigo": "02", "descricao": "PODER EXECUTIVO", "unidadeComposta": "02", "unidadeCompostaFormatada": "02", "unidadeSuperior": null }, { "id": 3, "codigo": "03", "descricao": "ASSESSORIA JURÍDICA", "unidadeComposta": "03", "unidadeCompostaFormatada": "03", "unidadeSuperior": null }, { "id": 4, "codigo": "04", "descricao": "ASSESSORIA DE PLANEJAMENTO", "unidadeComposta": "04", "unidadeCompostaFormatada": "04", "unidadeSuperior": null }, { "id": 5, "codigo": "05", "descricao": "SECRETARIA DE ADMINISTRAÇÃO", "unidadeComposta": "05", "unidadeCompostaFormatada": "05", "unidadeSuperior": null }, { "id": 6, "codigo": "06", "descricao": "SECRETARIA DE FINANÇAS", "unidadeComposta": "06", "unidadeCompostaFormatada": "06", "unidadeSuperior": null }, { "id": 7, "codigo": "07", "descricao": "SECRETARIA DE SAÚDE", "unidadeComposta": "07", "unidadeCompostaFormatada": "07", "unidadeSuperior": null }, { "id": 8, "codigo": "08", "descricao": "SECRETARIA DE SERVIÇO SOCIAL", "unidadeComposta": "08", "unidadeCompostaFormatada": "08", "unidadeSuperior": null }, { "id": 9, "codigo": "09", "descricao": "SECRETARIA DE EDUCAÇÃO", "unidadeComposta": "09", "unidadeCompostaFormatada": "09", "unidadeSuperior": null }, { "id": 10, "codigo": "10", "descricao": "SECRETARIA DE CULTURA", "unidadeComposta": "10", "unidadeCompostaFormatada": "10", "unidadeSuperior": null }, { "id": 11, "codigo": "11", "descricao": "SECRETARIA DE VIAÇÃO, OBRAS E SERVIÇOS URBANOS", "unidadeComposta": "11", "unidadeCompostaFormatada": "11", "unidadeSuperior": null }, { "id": 12, "codigo": "12", "descricao": "SECRETARIA DE AGRICULTURA E PECUÁRIA", "unidadeComposta": "12", "unidadeCompostaFormatada": "12", "unidadeSuperior": null }, { "id": 13, "codigo": "13", "descricao": "INSTITUTO DE PREVIDÊNCIA E ASSISTÊNCIA DO MUNICÍPIO DE ANGULO", "unidadeComposta": "13", "unidadeCompostaFormatada": "13", "unidadeSuperior": null }, { "id": 14, "codigo": "14", "descricao": "SERVIÇO AUTONOMO MUNICIPAL DE AGUA E ESGOTO", "unidadeComposta": "14", "unidadeCompostaFormatada": "14", "unidadeSuperior": null }, { "id": 15, "codigo": "15", "descricao": "SECRETARIA DE MEIO AMBIENTE", "unidadeComposta": "15", "unidadeCompostaFormatada": "15", "unidadeSuperior": null }, { "id": 16, "codigo": "16", "descricao": "SECRETARIA DE ESPORTES", "unidadeComposta": "16", "unidadeCompostaFormatada": "16", "unidadeSuperior": null }];
const sectors = generateSectorsByTemplate([
    ['RH - Recursos Humanos',
        [
            ['FL - Folha de Pagamento',
                [
                    ['FOP - Folha de Pagamento Operacional'],
                    ['FAD - Folha de Pagamento Administrativa']
                ]
            ],
            ['RCP - Recrutamento e Seleção',
                [
                    ['CT - Cargos e Títulos'],
                    ['PSC - Processo Seletivo'],
                    ['ENT - Entrevista',
                        [
                            ['ENT - Entrevista Individual',
                                [
                                    ['EO - Entrevista Operacional'],
                                ]
                            ],
                            ['PAN - Painel de Entrevistas'],
                        ]
                    ],
                    ['CND - Condução'],
                    ['ADM - Admissão'],
                    ['DMS - Desligamento']
                ]
            ],
            ['T&D - Treinamento e Desenvolvimento'],
            ['DP - Departamento Pessoal'],
        ]
    ],
    ['ADM - Administração',
        [
            ['COP - Compras',
                [
                    ['COT - Cotação',
                        [
                            ['COT - Cotação de Preços'],
                            ['COT - Cotação de Serviços']
                        ]
                    ],
                    ['PED - Pedido'],
                    ['RCE - Recebimento'],
                    ['PAG - Pagamento',
                        [
                            ['PAGF - Pagamento de Fornecedores',
                                [
                                    ['PAGM - Pagamento de Materiais'],
                                    ['PAGS - Pagamento de Serviços']
                                ]
                            ],
                            ['PAG - Pagamento de Serviços']
                        ]
                    ],
                ]
            ],
            ['ALM - Almoxarifado'],
            ['PTO - Patrimônio'],
            ['SEG - Segurança do Trabalho'],
        ]
    ],
    ['FIN - Financeiro'],
    ['COM - Comercial'],
    ['PRO - Produção',
        [
            ['PCP - Planejamento e Controle de Produção'],
            ['PC - Produção'],
            ['QAP - Qualidade e Processos'],
        ]
    ],
]);
function generateSectorsByTemplate(template, parentId = '') {
    return template.map(([name, children], index) => {
        const code = (index + 1).toString().padStart(3, '0');
        return {
            id: parentId + code,
            code: code,
            acronym: name.split('-')[0].trim(),
            name: name.split('-')[1].trim(),
            sectors: children ? generateSectorsByTemplate(children, parentId + code + '.') : []
        };
    });
}
;
function getSampleValueFromField(field) {
    if (field.type === FieldType.FIELD_SET) {
        return getFillFieldsValues(field.fields || []);
    }
    if (field.type === FieldType.MAP) {
        return {
            address: 'R. Duque de Caxias - Jardim Panorama, Sarandi - PR, Brasil',
            ref: '1084',
            position: {
                lat: -23.4319664,
                lng: -51.8741036
            }
        };
    }
    if (field.type === FieldType.FULL_ADDRESS) {
        return {
            'Cep|#CEP': '00000000',
            Logradouro: 'Rua Teste',
            Numero: '123',
            Bairro: 'Bairro Teste',
            Cidade: 'Cidade Teste',
            Estado: 'PR',
            Complemento: 'Complemento Teste'
        };
    }
    return FieldDataTypeMap.get(FieldTypeMap.get(field.type)?.dataType || FieldDataType.TEXT)?.sampleValue || null;
}

function and(...exp) {
    const result = exp.filter(e => e).join(';');
    return result ? `(${result})` : '';
}
function or(...exp) {
    const result = exp.filter(e => e).join(',');
    return result ? `(${result})` : '';
}
function inList(field, values) {
    if (!values || !values.length)
        return null;
    return `${field}=in=${"(" + (values.map(value => `"${encodeURIComponent(value)}"`).join(',') || '""') + ")"}`;
}
function notIn(field, values) {
    if (!values || !values.length)
        return null;
    return `${field}=out=${"(" + (values.map(value => `"${encodeURIComponent(value)}"`).join(',') || '""') + ")"}`;
}
function eq(field, value) {
    if (!value)
        return null;
    return cmp(field, '==', value.trim());
}
function diff(field, value) {
    if (!value)
        return null;
    return cmp(field, '!=', value.trim());
}
function gte(field, value) {
    if (!value)
        return null;
    return cmp(field, '>=', value.trim());
}
function lte(field, value) {
    if (!value)
        return null;
    return cmp(field, '<=', value.trim());
}
function gt(field, value) {
    if (!value)
        return null;
    return cmp(field, '>', value.trim());
}
function lt(field, value) {
    if (!value)
        return null;
    return cmp(field, '<', value.trim());
}
function like(field, value = '') {
    if (!value)
        return null;
    return cmp(field, '==', `*${value.trim()}*`);
}
function unnacentLike(field, value = '') {
    if (!value)
        return null;
    return cmp(field, '=acc=', `*${value.toLowerCase().trim()}*`);
}
function cmp(field, operator, value = '') {
    return `${field}${operator}"${encodeRSQLValue(value)}"`;
}
function encodeRSQLValue(value) {
    return encodeURIComponent(value.replace(/"/g, ''));
}

/**
 * Generated bundle index. Do not edit.
 */

export { DocumentsValidators, and, currentUser, diff, dynamicFormWithAllDefaultFields, dynamicFormWithAllFieldsNotRequired, eq, getFillFieldsValues, getFormControlFrom, getNameInitials, gt, gte, highlight, inList, isRequired, like, lt, lte, niceBytes, notIn, or, sectors, sortNumberNull, unidades, unnacentLike };
